import { useCallback, useState } from 'react';
import {
  CustomerServiceTag,
  QuoteApiModel,
  QuoteOptionApiModel,
  QuoteProviderAccountApiModel,
  QuoteProviderApiModel
} from '~/lib/api/smartsend/types';
import { CurrentSelectionType, DefaultQuoteRequestType } from './utils/CurrentSelectionType';
import { Tagged } from '../../Tagged';
import { P2GAlert, P2GBox } from '@parcel2go/core-ui';
import Loading from '../../../../../components/loading/Loading';
import { SmartSendProvider } from '~/lib/consts/providers';
import ProviderSelection from './ProviderSelection';
import { TruncateString } from '../../../lib/helpers/StringHelper';
import QuotePicker from './QuotePicker';
import { useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { P2GSidePanel } from '~/components/sidepanel';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import useOnLoad from '~/routes/use-on-load';

interface props {
  close: () => void;
  onSave?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  onUpdate?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  getQuotes: (request: DefaultQuoteRequestType) => Promise<QuoteApiModel[]>;
  currentSelection: CurrentSelectionType;
  estimatedValue?: number;
  quoteProviders: () => Promise<QuoteProviderApiModel[]>;
  loggingSections: string[];
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  getServiceTags: () => Promise<CustomerServiceTag[]>;
}

const QuoteModal: React.FC<props> = (props) => {
  const isXs = useP2GMediaQueryOnly('xs');
  const [selectedProviderAccount, setSelectedProviderAccount] = useState<QuoteProviderAccountApiModel>();
  const [providerAccounts, setProviderAccounts] = useState<QuoteProviderApiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProviderSelector, setShowProviderSelector] = useState(false);
  const [rawTags, setRawTags] = useState<CustomerServiceTag[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const internalUpdateServiceTagsCallback = useCallback(
    (existingTags: CustomerServiceTag[], add: CustomerServiceTag[], remove: CustomerServiceTag[]) => {
      add?.forEach((a) => {
        const existingService = existingTags.find((x) => x.ServiceSlug === a.ServiceSlug);
        if (existingService) {
          existingService.Tags = existingService.Tags.concat(a.Tags);
        } else {
          existingTags.push(a);
        }
      });

      remove?.forEach((a) => {
        const existingService = existingTags.find((x) => x.ServiceSlug === a.ServiceSlug);
        if (existingService) {
          existingService.Tags = existingService.Tags.filter((t) => !a.Tags.includes(t));
        }
      });

      setRawTags(existingTags);
      props.updateServiceTags(add, remove);
    },
    [props]
  );

  const updateServiceTagsCallback = useCallback(
    (add: CustomerServiceTag[], remove: CustomerServiceTag[]) =>
      internalUpdateServiceTagsCallback(rawTags, add, remove),
    [rawTags, internalUpdateServiceTagsCallback]
  );

  const toggleSpecificServiceTagCallback = useCallback(
    (toggle: CustomerServiceTag) => {
      if (
        rawTags.find(
          (x) => x.ServiceSlug === toggle.ServiceSlug && toggle.Tags.every((t) => x.Tags?.includes(t) == true)
        )
      ) {
        internalUpdateServiceTagsCallback(rawTags, [], [toggle]);
      } else {
        internalUpdateServiceTagsCallback(rawTags, [toggle], []);
      }
    },
    [rawTags, internalUpdateServiceTagsCallback]
  );

  useOnLoad(() => {
    const loadData = async () => {
      const qResp = await props.quoteProviders();

      if (qResp.length === 1) {
        setSelectedProviderAccount(qResp[0].Accounts[0]);
      } else {
        setShowProviderSelector(true);
      }
      setProviderAccounts(qResp);

      const tResp = await props.getServiceTags();
      setRawTags(tResp.filter((x) => x.GroupIdentifier === SmartSendProvider));

      setLoading(false);
    };
    loadData();
  });

  return (
    <>
      <P2GSidePanel
        open={!!selectedProviderAccount}
        onClose={props.close}
        anchor="bottom"
        maxWidth={false}
        height="95vh"
      >
        {selectedProviderAccount && (
          <>
            {loading ? (
              <P2GBox pt={2} pb={2}>
                <Loading />
              </P2GBox>
            ) : (
              <Tagged tag="panel-service">
                <QuotePicker
                  isXs={isXs}
                  toggleSpecificTag={toggleSpecificServiceTagCallback}
                  close={props.close}
                  currentSelection={props.currentSelection}
                  getQuotes={props.getQuotes}
                  tags={rawTags}
                  updateServiceTags={updateServiceTagsCallback}
                  loggingSections={props.loggingSections}
                  quoteProviders={providerAccounts}
                  selectedProviderAccount={selectedProviderAccount}
                  estimatedValue={props.estimatedValue}
                  onUpdate={props.onUpdate}
                  onSave={props.onSave}
                  onBack={() => {
                    if (isXs && providerAccounts.length > 1) {
                      setSelectedProviderAccount(null);
                      setShowProviderSelector(true);
                    } else {
                      props.close();
                    }
                  }}
                  onSwitchAccount={() => setShowProviderSelector(true)}
                />
              </Tagged>
            )}
          </>
        )}
      </P2GSidePanel>
      <P2GSidePanel
        open={showProviderSelector}
        onClose={() => {
          if (selectedProviderAccount) {
            setShowProviderSelector(false);
          } else {
            props.close();
          }
        }}
        anchor="right"
        title={t('CHOOSE_SERVICES_SIDEPANEL_TITLE', { defaultValue: 'Which services would you like to apply?' })}
        width="580px"
      >
        {showProviderSelector &&
          (loading ? (
            <P2GBox pt={2} pb={2}>
              <Loading />
            </P2GBox>
          ) : (
            <Tagged tag="panel-service">
              <ProviderSelection
                selectProviderAccount={(p) => {
                  setSelectedProviderAccount(p);
                  setShowProviderSelector(false);
                }}
                accounts={providerAccounts}
                currentSelection={props.currentSelection}
              >
                {props.currentSelection?.marketplaceService && (
                  <P2GAlert palette="info">
                    <Trans
                      i18nKey="QUOTE_MODAL_MARKETPLACE_SERVICE_ALERT"
                      t={t}
                      values={{ service: TruncateString(props.currentSelection?.marketplaceService, 20) }}
                      defaults="Your recipient chose <strong>{{service}}</strong> at checkout."
                    />
                  </P2GAlert>
                )}
              </ProviderSelection>
            </Tagged>
          ))}
      </P2GSidePanel>
    </>
  );
};
export default QuoteModal;
